
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import RegistrationModal from '../../components/auth/RegistrationModal';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function Subscribe() {
  const { isAuthenticated, user, loginWithSubscriberData } = useAuth();
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

  const handleRegistrationSuccess = (newUser: any) => {
    console.log('Registration successful:', newUser);
    // Properly authenticate the user with the subscriber data
    loginWithSubscriberData(newUser);
    setIsRegistrationModalOpen(false);
  };

  // SEO Schema.org data for subscribe page
  const subscriptionSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Subscribe to DBD Podcast",
    "description": "Join the DBD Podcast community for early access to episodes, exclusive content, and business insights from industry leaders.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/subscribe`,
    "mainEntity": {
      "@type": "Offer",
      "name": "DBD Podcast Subscription",
      "description": "Free subscription to DBD Podcast with early access to episodes and exclusive content",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "category": "Podcast Subscription",
      "itemOffered": {
        "@type": "Service",
        "name": "DBD Podcast Premium Access",
        "description": "Early access to episodes, exclusive show notes, full transcripts, and guest resources"
      }
    }
  };

  return (
    <>
      <div className="min-h-screen pt-16">
        <SEOHead
          title="Subscribe to DBD Podcast | Free Early Access & Exclusive Content"
          description="Join the DBD Podcast community for free. Get early access to episodes, exclusive show notes, full transcripts, and resources from business leaders and industry experts."
          keywords="subscribe DBD podcast, podcast subscription, early access episodes, exclusive content, business insights, free podcast subscription"
          ogTitle="Subscribe to DBD Podcast | Free Early Access & Exclusive Content"
          ogDescription="Join the DBD Podcast community for early access to episodes, exclusive content, and business insights from industry leaders."
          ogType="website"
          canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/subscribe`}
        />
        
        <SchemaOrg data={subscriptionSchema} />

        {/* Hero Section */}
        <section 
          className="relative py-20 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/c1982cbfd61ad46452bb16ef78c0577c.jpg')`
          }}
        >
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Join the DBD Community
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Get early access to episodes, exclusive content, and connect with business leaders 
                who are driven by data and delivered by design.
              </p>
              
              {isAuthenticated ? (
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 max-w-md mx-auto">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-circle-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">You're All Set!</h3>
                  <p className="text-gray-200 mb-4">
                    Welcome, {user?.user_metadata?.name || user?.email?.split('@')[0]}!
                  </p>
                  <p className="text-gray-300 text-sm">
                    You now have access to early episodes and exclusive content.
                  </p>
                </div>
              ) : (
                <button
                  onClick={() => setIsRegistrationModalOpen(true)}
                  className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-200 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line mr-3"></i>
                  Subscribe Now - It's Free
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">What You'll Get</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join thousands of business leaders who trust DBD Podcast for cutting-edge insights
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
              {/* Early Access */}
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-time-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Early Access</h3>
                <p className="text-gray-600">
                  Be the first to hear new episodes before they go public. Get exclusive early access to all DBD Podcast content.
                </p>
              </div>

              {/* Exclusive Show Notes */}
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items.center justify-center mb-6">
                  <i className="ri-file-text-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Exclusive Show Notes</h3>
                <p className="text-gray-600">
                  Get detailed show notes, key takeaways, and additional insights that aren't available to regular listeners.
                </p>
              </div>

              {/* Guest Resources */}
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items.center justify-center mb-6">
                  <i className="ri-links-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Guest Resources</h3>
                <p className="text-gray-600">
                  Access exclusive resources, links, and materials shared by our guests that complement each episode.
                </p>
              </div>

              {/* Full Transcripts */}
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items.center justify-center mb-6">
                  <i className="ri-article-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Full Transcripts</h3>
                <p className="text-gray-600">
                  Get complete episode transcripts for easy reference, note-taking, and accessibility.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get started in minutes with our secure email verification process
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-500 rounded-full flex items.center justify-center mx-auto mb-6 text-white font-bold text-2xl">
                    1
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Fill Out the Form</h3>
                  <p className="text-gray-600">
                    Provide your name, email, country, and industry. All information is kept 
                    secure and used only to personalize your experience.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-500 rounded-full flex items.center justify-center mx-auto mb-6 text-white font-bold text-2xl">
                    2
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Check Your Email</h3>
                  <p className="text-gray-600">
                    We'll send a 6-digit verification code to your inbox. Check your spam 
                    folder if you don't see it within a few minutes.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-500 rounded-full flex items.center justify-center mx-auto mb-6 text-white font-bold text-2xl">
                    3
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Start Listening</h3>
                  <p className="text-gray-600">
                    Once verified, you immediately get access to early episodes and exclusive 
                    content. Welcome to the community!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Security Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items.center justify-center mx-auto mb-8">
                <i className="ri-shield-check-line text-green-600 text-3xl"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Your Privacy Matters</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We use industry-standard email verification to keep your account secure. 
                Your information is never shared with third parties, and you can unsubscribe 
                at any time. We comply with GDPR, CAN-SPAM, and other privacy regulations.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div className="flex items.center justify-center space-x-2 text-gray-700">
                  <i className="ri-lock-line text-green-600"></i>
                  <span>Secure Email Verification</span>
                </div>
                <div className="flex items.center justify-center space-x-2 text-gray-700">
                  <i className="ri-user-settings-line text-green-600"></i>
                  <span>GDPR Compliant</span>
                </div>
                <div className="flex items.center justify-center space-x-2 text-gray-700">
                  <i className="ri-spam-2-line text-green-600"></i>
                  <span>No Spam Policy</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        {!isAuthenticated && (
          <section className="py-20 bg-gradient-to-r from-amber-500 to-amber-600">
            <div className="container mx-auto px-6 text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-4xl font-bold text-white mb-6">
                  Ready to Join 1,000+ Subscribers?
                </h2>
                <p className="text-xl text-amber-100 mb-8">
                  Get instant access to our latest episode and start your journey with 
                  exclusive business insights today.
                </p>
                <button
                  onClick={() => setIsRegistrationModalOpen(true)}
                  className="inline-flex items.center bg-white text-amber-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-xl text-lg transition-all duration-200 transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-rocket-line mr-3"></i>
                  Get Started Now
                </button>
                <p className="text-amber-200 text-sm mt-4">
                  Free forever • No credit card required • Unsubscribe anytime
                </p>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        onRegistrationSuccess={handleRegistrationSuccess}
      />
    </>
  );
}
